import React, { createContext, useCallback, useContext, useLayoutEffect, useState } from 'react'
import { Composable } from '../../utils/Composable'

export type GlobalSearchContextProps = {
  globalSearchVisible: boolean
  setGlobalSearchVisible: (isVisible: boolean) => void
  showArchivedAccounts: boolean
  setShowArchivedAccounts: (show: boolean) => void
}

export const GlobalSearchContext = createContext({} as GlobalSearchContextProps)
export const useGlobalSearch = () => useContext(GlobalSearchContext)

const ARCHIVED_ACCOUNTS_STORAGE_KEY = 'breezy-search-show-archived-accounts'

export const GlobalSearchWrapper = React.memo<Composable>(({ children }) => {
  const [globalSearchVisible, setGlobalSearchVisible] = useState(false)
  const [showArchivedAccounts, setShowArchivedAccountsState] = useState(() => {
    // Load from localStorage, defaulting to false (hide archived accounts)
    try {
      const stored = localStorage.getItem(ARCHIVED_ACCOUNTS_STORAGE_KEY)
      return stored ? JSON.parse(stored) : false
    } catch {
      return false
    }
  })

  const setShowArchivedAccounts = useCallback((show: boolean) => {
    setShowArchivedAccountsState(show)
    // Persist to localStorage
    try {
      localStorage.setItem(ARCHIVED_ACCOUNTS_STORAGE_KEY, JSON.stringify(show))
    } catch {
      // Silently fail if localStorage is not available
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: setGlobalSearchVisible dependency can be removed
  const toggleGlobalSearch = useCallback(() => {
    setGlobalSearchVisible(isVisible => !isVisible)
  }, [setGlobalSearchVisible])

  useLayoutEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        toggleGlobalSearch()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [toggleGlobalSearch])

  return (
    <GlobalSearchContext.Provider
      value={{
        globalSearchVisible,
        setGlobalSearchVisible,
        showArchivedAccounts,
        setShowArchivedAccounts,
      }}
    >
      {children}
    </GlobalSearchContext.Provider>
  )
})

GlobalSearchWrapper.displayName = 'GlobalSearchWrapper'
